import React, { ReactElement, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector, RootStateOrAny } from 'react-redux';
import Ticker from 'react-ticker';
import CardHeader from './shared/CardHeader';
import GoogleLogo from '../assets/images/auth-providers/Google Logo.png';
import { ReactComponent as AppleLogo } from '../assets/images/auth-providers/apple.svg';
import { ReactComponent as ArrowDownIcon } from '../assets/images/misc-icons/warped-chev-down.svg';
import queryString from 'query-string';
import useSocialAuth from '../services/auth/useSocialAuth';
import useUser from '../services/auth/useUser';
import { getDeviceAndLocationDetails, getDeviceFingerprint, isIOS } from '../utils/device';
import {
  setDeviceDetails,
  setFingerprintDetails,
  setRedirectFrom,
  setRedirectTo,
  setIsLoading,
  setAuthCodeToken,
  setFrom2FARedirect,
  setIsIframe,
} from '../store/auth/actions';
import {
  setEmail,
  setIsNewUser,
  setPhoneNumberDetails,
  setReferralCode,
  setReferredLinkParams,
  setSignInMethod,
  setUser,
} from '../store/user/actions';
import useOnLogout from '../library/hooks/user/useOnLogout';
import useSocialAuthCallback from '../library/hooks/auth/useSocialAuthCallback';
import { SignInMethod } from '../library/enums';
import AppWrapper from './shared/AppWrapper';
import useAuth from '../services/auth/useAuth';
import { useErrorHandler } from '../utils/error/error-handler';
import { useGoogleOneTapLogin } from 'react-google-one-tap-login';
import { isTabletBrowser, isMobileOrTabletBrowser } from '../utils/device';
import { IGoogleEndPointResponse } from 'react-google-one-tap-login/dist/types/types';
import AlertOverlayContainer from '../components/common/notifications/AlertOverlayContainer';
import { setFSUser } from '../services/fullstory';
import { whitelistRedirectTo } from '../utils/redirect/whitelistRedirect';

export default function Login(props: any): ReactElement {
  const [showExtraOptions, setShowExtraOptions] = useState(false);
  const [emailByDevice, setEmailByDevice] = useState('');
  const [phoneByDevice, setPhoneByDevice] = useState('');
  const [hideGoogleMethod, setHideGoogleMethod] = useState(false);
  const [redirectToPower, setRedirectToPower] = useState(false);
  const [redirectToUnified, setRedirectToUnified] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const onLogout = useOnLogout();
  const { openOAuthSignInWindow } = useSocialAuth();
  const { getUserDetailsByDevice } = useUser();
  const { referralCode, referredLinkParams } = useSelector((state: RootStateOrAny) => state.user);
  const { fingerPrintDetails, isLoading, deviceDetails, redirectFrom, redirectTo, isIframe } = useSelector(
    (state: RootStateOrAny) => state.auth
  );
  const onSocialAuthCallback = useSocialAuthCallback();
  const isAndroid = /Android/.test(navigator.userAgent);
  const { handleError, throwAlert } = useErrorHandler();
  const { handleUserRedirect, verifyEmailSend2FA, verifyOneTapUser } = useAuth();
  const { FLUZ_AUTH_PORTAL_GOOGLE_OAUTH_CLIENT_ID, FLUZ_ENV_NAME } = process.env;
  const isStaging = FLUZ_ENV_NAME !== 'production';
  const title = isStaging ? 'Fluz Staging' : 'Log in or sign up';

  useGoogleOneTapLogin({
    onError: throwAlert,
    onSuccess: onGoogleOneTapLogin,
    googleAccountConfigs: {
      client_id: FLUZ_AUTH_PORTAL_GOOGLE_OAUTH_CLIENT_ID as string,
    },
  });

  useEffect(() => {
    if (!referredLinkParams) {
      dispatch(setReferredLinkParams(props.referredLinkParams));
    }

    if (!referralCode) {
      dispatch(setReferralCode(props.referredLinkParams?.code));
    }
  }, [props.referredLinkParams]);

  useEffect(() => {
    // Hide Google method when user is within a webview (e.g. Instagram/TikTok) because Google authentication is not allowed within webview.
    if (
      ['Mobile Safari UI/WKWebView', 'Instagram', 'Chrome Mobile WebView'].includes(fingerPrintDetails?.browserName)
    ) {
      setHideGoogleMethod(true);
    }
  }, [fingerPrintDetails]);

  useEffect(() => {
    async function initDeviceDetails() {
      const qs = queryString.parse(location.search);
      // Mobile will pass along the device details for the device via the mobileDeviceDetails qs.
      try {
        if (qs.mobileDeviceDetails) {
          const mobileDeviceDetails = JSON.parse(qs.mobileDeviceDetails as string);
          dispatch(setDeviceDetails(mobileDeviceDetails));
          const { email, phoneNumber } = await getUserDetailsByDevice(mobileDeviceDetails?.deviceId);
          setEmailByDevice(email);
          setPhoneByDevice(phoneNumber);
        } else {
          const deviceDetails = await getDeviceAndLocationDetails();
          dispatch(setDeviceDetails(deviceDetails));
          const { email, phoneNumber } = await getUserDetailsByDevice(deviceDetails?.deviceId);
          setEmailByDevice(email);
          setPhoneByDevice(phoneNumber);
        }
      } catch (error) {
        setEmailByDevice('');
        setPhoneByDevice('');
      }
      const fingerprintDetails = await getDeviceFingerprint();
      dispatch(setFingerprintDetails(fingerprintDetails));

      dispatch(setIsIframe(window.self !== window.top));
      // We will only set the redirectFrom if the isPower/isUnified qs is passed along. If not, we will not do anything.
      // This will allow for persistance of origin until overridden in case the auth portal is refreshed accidentally
      if (typeof qs.isPower === 'string' || typeof qs.isUnified === 'string') {
        if (qs.isPower === 'true') {
          const url = whitelistRedirectTo({ redirectFrom: 'POWER' });

          setRedirectToPower(true);
          dispatch(setRedirectFrom('POWER'));
          dispatch(setRedirectTo(url));
          history.replace('/');
        } else if (qs.isUnified === 'true') {
          const rawReferrer = qs.referrer;
          let referrer = '';

          if (typeof rawReferrer === 'string') {
            referrer = rawReferrer;
          }

          const url = whitelistRedirectTo({ redirectFrom: 'UNIFIED', referrer });
          setRedirectToUnified(true);
          dispatch(setRedirectFrom('UNIFIED'));
          dispatch(setRedirectTo(url));
          if (qs.is2FA === 'true') {
            dispatch(setFrom2FARedirect(true));
            dispatch(setIsNewUser(false));
            onClickContinueWithPhoneNumber(true);
          } else {
            history.replace('/');
          }
        } else {
          const url = whitelistRedirectTo({ redirectFrom: 'MOBILE' });

          dispatch(setRedirectFrom('MOBILE'));
          dispatch(setRedirectTo(url));
        }
      }
      if (qs.branchParams) {
        /** START Handle referral code data  */
        if (JSON.parse(qs.branchParams as string)?.code) {
          // Set referral code using data passed in from mobile FE (in-app browser)
          dispatch(setReferralCode(JSON.parse(qs.branchParams as string)?.code));
        }
        /** END */

        /** START Handle Impact data */
        // Set impact info using data passed in from mobile FE to in-app browser.
        // Note: If opening Impact link outside the mobile in-app browser, then initApp() will handle setting the Impact data.
        if (JSON.parse(qs.branchParams as string)['+non_branch_link']) {
          // Ignore the key name 'non_branch_link'. This IS technically a branch link.
          const cleanedUrl = decodeURIComponent(JSON.parse(qs.branchParams as string)['+non_branch_link']).replace(
            /\\/g,
            ''
          );
          const cleanedParams = new URL(cleanedUrl as string).searchParams;
          const decodedParams = decodeURIComponent(cleanedParams as unknown as string);
          const params = new URLSearchParams(decodedParams);
          const referredLinkParams: any = {};
          for (const [key, value] of params.entries()) {
            referredLinkParams[key] = value;
          }

          dispatch(setReferredLinkParams(referredLinkParams));
        }
        /** END */
      }
      if (qs.is2FA !== 'true') {
        onLogout();
      }
    }
    initDeviceDetails();
  }, []);

  async function onGoogleOneTapLogin(googleRes: IGoogleEndPointResponse) {
    dispatch(setIsLoading(true));
    try {
      const verifyGoogleRes = await verifyOneTapUser({
        email: googleRes.email,
        sub: googleRes.sub,
        deviceDetails,
        fingerprintData: fingerPrintDetails,
        redirectFrom,
        redirectTo,
      });
      if (verifyGoogleRes.success && verifyGoogleRes.skip2FA && verifyGoogleRes.uri) {
        setFSUser(verifyGoogleRes.userId, googleRes.email, verifyGoogleRes.firstName, verifyGoogleRes.lastName);
        handleUserRedirect(verifyGoogleRes.uri, !!verifyGoogleRes.hasPowerTag);
      } else {
        const verifyEmailResponse = await verifyEmailSend2FA({
          email: googleRes.email,
          deviceId: deviceDetails?.deviceId,
        });

        dispatch(
          setUser({
            email: googleRes.email,
            firstName: googleRes.given_name,
            lastName: googleRes.family_name,
            avatarUrl: googleRes.picture,
            googleId: googleRes.sub,
            phoneNumberFormatted: verifyEmailResponse.phoneNumber,
            isNewUser: verifyEmailResponse.isNewUser,
            hasPowerTag: verifyEmailResponse.isNewUser ? false : !!verifyEmailResponse.hasPowerTag,
            signInMethod: SignInMethod.GOOGLE,
          })
        );

        if (verifyEmailResponse.isNewUser) {
          //If it's a new user, send to phone number view
          history.push('/login/phone-number');
        } else {
          //If it's an existing user, set auth code token and send to 2FA code view
          dispatch(setAuthCodeToken(verifyEmailResponse.token));
          history.push('/login/enter-twofa-code');
        }
      }
    } catch (error: any) {
      if (error?.name === 'HTTPError' && error?.response.status === 403) {
        throwAlert(error?.response.statusText, 'Unable to log in. Please contact support for more information.');
      } else {
        handleError(error);
      }
    } finally {
      dispatch(setIsLoading(false));
    }
  }

  async function onClickContinueWithGoogle(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): Promise<void> {
    onLogout(); // clear store when user begins registration/login flow
    dispatch(setSignInMethod(SignInMethod.GOOGLE));
    event.preventDefault();
    setEmailPhoneByDevice();
    dispatch(setIsLoading(true));
    openOAuthSignInWindow('google', onSocialAuthCallback);
  }

  async function onClickContinueWithPhoneNumber(isRedirectedFrom2FA: boolean) {
    if (!isRedirectedFrom2FA) {
      onLogout();
    } // clear store when user begins registration/login flow
    dispatch(setSignInMethod(SignInMethod.PHONE));
    setEmailPhoneByDevice();
    history.push('/login/phone-number');
  }
  async function onClickContinueWithEmail() {
    onLogout(); // clear store when user begins registration/login flow
    dispatch(setSignInMethod(SignInMethod.EMAIL));
    setEmailPhoneByDevice();
    history.push('/login/email');
  }

  function setEmailPhoneByDevice() {
    if (emailByDevice) {
      dispatch(setEmail(emailByDevice));
    }
    if (phoneByDevice) {
      dispatch(setPhoneNumberDetails('.'.repeat(phoneByDevice.length), phoneByDevice, phoneByDevice, '')); // need to set phoneNumberExpectedFormat for button on phone number view to work
    }
  }

  return (
    <AppWrapper isLoginScreen={true}>
      <div data-state={isIframe ? 'iframe' : 'standalone'} className="page-background">
        <div className="container-fluid card-container">
          <div className="card-group">
            <div className="card">
              <AlertOverlayContainer />
              <div className="px-3">
                <CardHeader isInitialPage isStaging={isStaging} />
                <div className="row header-row">
                  <div className="card-registration-header">{title}</div>
                </div>
                <div className="row" style={{ display: hideGoogleMethod ? 'none' : '' }}>
                  <div className="col card-col">
                    <button
                      id="google-auth-button"
                      type="submit"
                      className="btn-registration-method"
                      disabled={isLoading}
                      onClick={onClickContinueWithGoogle}
                    >
                      <div className="d-flex align-items-center justify-content-center">
                        <img src={GoogleLogo} style={{ width: 20, height: 20 }} className="p-0" />
                        <div className="col">Continue with Google</div>
                      </div>

                      {isLoading && (
                        <div
                          className="spinner-border btn-form-loading"
                          role="status"
                          style={{
                            width: 20,
                            height: 20,
                            margin: 0,
                          }}
                        >
                          <span className="sr-only">Loading...</span>
                        </div>
                      )}
                    </button>
                  </div>
                </div>
                <div className="row">
                  <div className="col card-col">
                    <button
                      id="phone-number-button"
                      type="submit"
                      className="btn btn-registration-method"
                      disabled={isLoading}
                      onClick={() => onClickContinueWithPhoneNumber(false)}
                    >
                      <div className="col">Continue with phone number</div>
                    </button>
                  </div>
                </div>
                {!redirectToPower && !redirectToUnified && isMobileOrTabletBrowser() && isIOS() && (
                  <div className="row">
                    <div className="col card-col">
                      <button
                        id="login-with-apple-button"
                        type="submit"
                        className="btn btn-registration-method"
                        disabled={isLoading}
                        onClick={() => {
                          window.location.assign('fluz://?loginWithPassword=true&loginWithApple=true');
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-center">
                          <AppleLogo className="p-0" height={20} width={20} />
                          <div className="col">Continue with Apple</div>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
                {showExtraOptions && (
                  <>
                    <div className="row">
                      <div className="col card-col">
                        <button
                          id="email-button"
                          type="submit"
                          className="btn btn-registration-method extra-option-button"
                          disabled={isLoading}
                          onClick={onClickContinueWithEmail}
                        >
                          <div className="col">Continue with email</div>
                        </button>
                      </div>
                    </div>
                    {/* TODO: DISABLED AS PART OF https://fluz.atlassian.net/browse/FLUZ-11786
                    SHOULD BE BROUGHT BACK LATER WHEN THE SPECIFIC FLOW IS DECIDED */}
                    {/* show the button for power users and mobile browsers */}
                    {/* {(redirectToPower || redirectToUnified || isMobileOrTabletBrowser()) && (
                      <div className="row">
                        <div className="col card-col">
                          <button
                            id="login-with-password-button"
                            type="submit"
                            className="btn btn-registration-method extra-option-button"
                            disabled={isLoading}
                            onClick={() => {
                              // Temporary quick fix.
                              if (redirectToUnified) {
                                const url = redirectTo.replace('login', 'legacy-login');
                                window.location.assign(url);
                              } else {
                                window.location.assign(redirectTo);
                              }
                            }}
                          >
                            <div className="col">Log in with password</div>
                          </button>
                        </div>
                      </div>
                    )} */}
                  </>
                )}
                {!showExtraOptions && (
                  <div className="row" style={{ marginRight: -1, marginLeft: -1 }}>
                    <div className="col d-flex justify-content-center align-items-center">
                      <button
                        id="more-options-button"
                        type="submit"
                        disabled={isLoading}
                        className="btn btn-more-options"
                        onClick={() => {
                          setShowExtraOptions(true);
                        }}
                      >
                        <div className="card-body card-more-options">More options</div>
                        <ArrowDownIcon className="arrow-down-icon" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div
          className={`marquee-text-container ${isAndroid ? 'android-marquee-text-container' : ''} ${
            isTabletBrowser() ? 'd-none' : ''
          }`}
        >
          <Ticker height="85">{() => <div className="marquee-text">CASHBACK.EVERYWHERE.</div>}</Ticker>
        </div>
      </div>
    </AppWrapper>
  );
}
